// src/pages/AssetManager.jsx
// Итерация 9.6: Tabs «Структура / Модуль» + StructureViewer + Module3DViewer.
// Итерация 9.7: расчёт статистики через utils/schemeStats (поддержка base_stats у модулей).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  Alert, Snackbar, CircularProgress, Chip, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Tooltip, Autocomplete,
  TablePagination, Paper, Tabs, Tab, List, ListItemButton, ListItemText,
  Accordion, AccordionSummary, AccordionDetails, Stack,
} from '@mui/material';
import {
  RocketLaunch as ShipIcon, Business as StructureIcon,
  DirectionsCar as VehicleIcon,
  Add as AddIcon, Save as SaveIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Build as BuildIcon, Science as ScienceIcon,
  Power as PowerIcon, People as PeopleIcon, AttachMoney as CostIcon,
  Scale as WeightIcon, Search as SearchIcon, Edit as EditIcon,
  Pause as PauseIcon, RocketLaunch as LaunchIcon,
  Engineering as EngIcon, Dock as DockIcon,
  LibraryAdd as LibraryAddIcon, Category as CategoryIcon,
  ContentCopy as CopyIcon, CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Architecture as ArchitectureIcon,
  AutoAwesome as AutoAwesomeIcon,
  Tune as TuneIcon, Handyman as HandymanIcon,
  Lock as LockIcon,
  ViewInAr as ViewInArIcon,
} from '@mui/icons-material';
import konstructGateway from '../api/konstructGateway';
import { calculateSchemeStats } from '../utils/schemeStats';

// Common layout
import PageShell from '../components/common/PageShell';
import PagePanel from '../components/common/PagePanel';
import StatCard from '../components/common/StatCard';

// RBAC
import { isAdmin } from '../utils/accessControl';

// Компоненты
import ModuleEditor from '../components/asset/ModuleEditor';
import StructureShipEditor from '../components/asset/StructureShipEditor';
import ModuleDesigner from '../components/asset/ModuleDesigner';
import GenerationWizard from '../components/asset/GenerationWizard';
import ModuleLibraryView from '../components/asset/ModuleLibraryView';
// 🔥 Итерация 9.6: 3D-вьюверы
import Module3DViewer from '../components/asset/Module3DViewer';
import StructureViewer from '../components/world/StructureViewer';

// ============================================================================
// Константы
// ============================================================================
const SYSTEM_OWNER = '0';

const ASSET_CONFIG = {
  ship_scheme: {
    label: 'Чертежи кораблей', icon: <ShipIcon />, color: '#1976d2',
    isScheme: true, isActive: false, isAggregate: false,
    listMethod: 'listShipSchemes', getMethod: 'getShipScheme',
    createMethod: 'createShipScheme', updateMethod: 'updateShipScheme',
    deleteMethod: 'deleteShipScheme', moduleType: 'ship',
  },
  structure_scheme: {
    label: 'Чертежи структур', icon: <ArchitectureIcon />, color: '#2e7d32',
    isScheme: true, isActive: false, isAggregate: true,
    listMethod: 'listStructureSchemes', getMethod: null,
    createMethod: null, updateMethod: null, deleteMethod: null,
    moduleType: 'structure',
  },
  vehicle_scheme: {
    label: 'Чертежи транспорта', icon: <VehicleIcon />, color: '#ed6c02',
    isScheme: true, isActive: false, isAggregate: false,
    listMethod: 'listVehicleSchemes', getMethod: 'getVehicleScheme',
    createMethod: 'createVehicleScheme', updateMethod: 'updateVehicleScheme',
    deleteMethod: 'deleteVehicleScheme', moduleType: 'vehicle',
  },
  space_station: {
    label: 'Космические станции', icon: <StructureIcon />, color: '#0288d1',
    isScheme: false, isActive: true, isAggregate: false,
    listMethod: 'listActiveSpaceStations', getMethod: 'getActiveSpaceStation',
    updateMethod: 'updateStation', deleteMethod: 'deleteStation',
    convertMethod: 'convertStationToScheme',
    checkModernizationMethod: 'checkStationModernization',
    applyModernizationMethod: 'applyStationModernization',
    getModulesMethod: 'getStationAvailableModules',
    moduleType: 'station',
  },
  spaceport: {
    label: 'Космопорты', icon: <DockIcon />, color: '#d32f2f',
    isScheme: false, isActive: true, isAggregate: false,
    listMethod: 'listActiveSpaceports', getMethod: 'getSpaceport',
    updateMethod: 'updateSpaceport', deleteMethod: 'deleteSpaceport',
    moduleType: 'spaceport',
  },
  shipyard: {
    label: 'Верфи', icon: <BuildIcon />, color: '#7b1fa2',
    isScheme: false, isActive: true, isAggregate: false,
    listMethod: 'listActiveShipyards', getMethod: 'getShipyard',
    updateMethod: 'updateShipyard', deleteMethod: 'deleteShipyard',
    convertMethod: 'convertShipyardToScheme',
    checkModernizationMethod: 'checkShipyardModernization',
    applyModernizationMethod: 'applyShipyardModernization',
    getModulesMethod: 'getShipyardAvailableModules',
    moduleType: 'shipyard',
  },
  civil_structure: {
    label: 'Гражданские структуры', icon: <ArchitectureIcon />, color: '#388e3c',
    isScheme: false, isActive: true, isAggregate: false,
    listMethod: 'listActiveCivilStructures', getMethod: 'getCivilStructure',
    updateMethod: 'updateCivilStructure', deleteMethod: 'deleteCivilStructure',
    convertMethod: 'convertCivilStructureToScheme',
    moduleType: 'civil_structure',
  },
  military_structure: {
    label: 'Военные структуры', icon: <EngIcon />, color: '#f57c00',
    isScheme: false, isActive: true, isAggregate: false,
    listMethod: 'listActiveMilitaryStructures', getMethod: 'getMilitaryStructure',
    updateMethod: 'updateMilitaryStructure', deleteMethod: 'deleteMilitaryStructure',
    convertMethod: 'convertMilitaryStructureToScheme',
    moduleType: 'military_structure',
  },
  ship: {
    label: 'Корабли', icon: <ShipIcon />, color: '#1976d2',
    isScheme: false, isActive: true, isAggregate: false,
    listMethod: 'listActiveShips', getMethod: 'getActiveShip',
    updateMethod: 'updateShip', deleteMethod: 'deleteShip',
    convertMethod: 'convertShipToScheme',
    checkModernizationMethod: 'checkShipModernization',
    applyModernizationMethod: 'applyShipModernization',
    getModulesMethod: 'getShipAvailableModules',
    moduleType: 'ship',
  },
  vehicle: {
    label: 'Транспорт', icon: <VehicleIcon />, color: '#ed6c02',
    isScheme: false, isActive: true, isAggregate: false,
    listMethod: 'listActiveVehicles', getMethod: 'getVehicle',
    updateMethod: 'updateVehicle', deleteMethod: 'deleteVehicle',
    convertMethod: 'convertVehicleToScheme',
    moduleType: 'vehicle',
  },
};

const ASSET_TYPES = [
  { key: 'ship_scheme', label: 'Чертежи кораблей', icon: <ShipIcon /> },
  { key: 'structure_scheme', label: 'Чертежи структур', icon: <ArchitectureIcon /> },
  { key: 'vehicle_scheme', label: 'Чертежи транспорта', icon: <VehicleIcon /> },
  { key: 'space_station', label: 'Космические станции', icon: <StructureIcon /> },
  { key: 'spaceport', label: 'Космопорты', icon: <DockIcon /> },
  { key: 'shipyard', label: 'Верфи', icon: <BuildIcon /> },
  { key: 'civil_structure', label: 'Гражданские структуры', icon: <ArchitectureIcon /> },
  { key: 'military_structure', label: 'Военные структуры', icon: <EngIcon /> },
  { key: 'ship', label: 'Корабли', icon: <ShipIcon /> },
  { key: 'vehicle', label: 'Транспорт', icon: <VehicleIcon /> },
];

const SIZE_TIERS = ['tiny', 'small', 'medium', 'large', 'huge', 'capital', 'megastructure'];
const TECH_LEVELS = Array.from({ length: 10 }, (_, i) => i + 1);
const SCHEME_STATUSES = ['draft', 'active', 'deprecated', 'archived', 'testing'];
const ACTIVE_STATUSES = ['active', 'under_construction', 'operational', 'damaged', 'maintenance', 'stored'];

// ============================================================================
// Хелперы: RBAC по владельцу
// ============================================================================
const getOwnership = (obj, myCharacterIds) => {
  if (!obj) return 'none';
  const cid = obj.character_id;
  if (!cid || cid === SYSTEM_OWNER) return 'system';
  if (myCharacterIds.includes(String(cid))) return 'mine';
  return 'other';
};

// ============================================================================
// StatusChip
// ============================================================================
function StatusChip({ status }) {
  const map = {
    draft: { label: 'Черновик', color: 'default' },
    active: { label: 'Активен', color: 'success' },
    deprecated: { label: 'Устарел', color: 'error' },
    archived: { label: 'В архиве', color: 'warning' },
    testing: { label: 'Тестируется', color: 'info' },
    under_construction: { label: 'Строится', color: 'warning' },
    operational: { label: 'Работает', color: 'success' },
    damaged: { label: 'Повреждён', color: 'error' },
    maintenance: { label: 'На обслуживании', color: 'info' },
    stored: { label: 'На хранении', color: 'default' },
  };
  const c = map[status] || { label: status || '—', color: 'default' };
  return (
    <Chip
      size="small"
      label={c.label}
      color={c.color}
      variant="outlined"
      sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem', px: 0.5 } }}
    />
  );
}

// ============================================================================
// Основной компонент
// ============================================================================
export default function AssetManager() {
  const auth = useSelector((s) => s.auth);
  const isAuthenticated = auth?.isAuthenticated;
  const user = auth?.user;

  const canManageAll = isAdmin(auth);
  const isLoggedIn = isAuthenticated;

  const myCharacterIds = useMemo(
    () =>
      (auth?.access?.characters || [])
        .map((c) => c.character_id)
        .filter((id) => id && id !== SYSTEM_OWNER)
        .map(String),
    [auth?.access?.characters]
  );

  // --- Состояние ---
  const [viewMode, setViewMode] = useState('hangar'); // hangar | dock
  const [activeType, setActiveType] = useState('ship_scheme');
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [activeTab, setActiveTab] = useState('assets'); // assets | library

  // 🔥 Итерация 9.6: вкладка «Структура / Модуль» (внутри assets)
  const [viewTab, setViewTab] = useState('structure'); // structure | module

  const [moduleLibrary, setModuleLibrary] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState('view'); // view | edit
  const [statistics, setStatistics] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const [constructionRequirements, setConstructionRequirements] = useState(null);
  const [maintenanceCost, setMaintenanceCost] = useState(null);
  const [moduleSlots, setModuleSlots] = useState(null);

  const [modernizationInfo, setModernizationInfo] = useState(null);
  const [availableModules, setAvailableModules] = useState([]);
  const [modernizationDialogOpen, setModernizationDialogOpen] = useState(false);
  const [selectedModules, setSelectedModules] = useState([]);

  // 🔥 Итерация 9.6: выбранный модуль для Module3DViewer
  const [selectedModule, setSelectedModule] = useState(null);

  const [generationWizardOpen, setGenerationWizardOpen] = useState(false);
  const [generationMode, setGenerationMode] = useState('auto');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [addModuleDialog, setAddModuleDialog] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');

  const [moduleDesignerOpen, setModuleDesignerOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleDesignerMode, setModuleDesignerMode] = useState('create');

  const showSnackbar = (msg, sev = 'info') => setSnackbar({ open: true, message: msg, severity: sev });

  const currentConfig = ASSET_CONFIG[activeType];
  const isScheme = currentConfig?.isScheme === true;
  const isActive = currentConfig?.isActive === true;

  // === RBAC: текущий объект ===
  const ownership = getOwnership(currentData, myCharacterIds); // system | mine | other | none
  const canManageCurrent = canManageAll || ownership === 'mine';

  const canManageModule = (mod) => {
    if (canManageAll) return true;
    const o = getOwnership(mod, myCharacterIds);
    return o === 'mine';
  };

  // ==========================================================================
  // Статистика
  // ==========================================================================
  const calculateStats = (data) => {
    if (!data) return;

    // 🔥 Итерация 9.7: единый расчёт через utils/schemeStats.
    // Поддерживает base_stats у модулей (world_manager) и корневые поля,
    // fallback на current_value / power_output / crew_required объекта.
    const stats = calculateSchemeStats(data);
    setStatistics(stats);

    const modules = data.installed_modules || data.modules || [];
    const totalSlots = data.max_module_slots || data.max_modules || data.total_module_slots || 100;
    setModuleSlots({ total: totalSlots, used: modules.length, free: totalSlots - modules.length });

    setConstructionRequirements({
      materials: data.required_materials || ['steel', 'electronics'],
      tools: data.required_tools || ['welding_torch', 'diagnostic_tools'],
      skills: data.required_skills || ['engineering', 'construction'],
      time: data.construction_time || 24,
    });
    setMaintenanceCost({
      daily: Math.round((stats?.cost || 0) * 0.001),
      weekly: Math.round((stats?.cost || 0) * 0.005),
      monthly: Math.round((stats?.cost || 0) * 0.02),
    });
  };

  // ==========================================================================
  // Загрузка списка
  // ==========================================================================
  const loadAssets = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    try {
      const config = ASSET_CONFIG[activeType];
      if (!config) { setAssets([]); return; }
      const method = konstructGateway[config.listMethod];
      if (!method) { setAssets([]); return; }

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await method.call(konstructGateway, params);

      let list = [];
      let total = 0;
      if (config.isActive) {
        if (Array.isArray(response)) { list = response; total = list.length; }
        else if (response?.data && Array.isArray(response.data)) { list = response.data; total = response.total || list.length; }
        else if (response?.status === 'success' && response.data) { list = Array.isArray(response.data) ? response.data : [response.data]; total = response.meta?.total || list.length; }
        else if (response?.shipyards) { list = response.shipyards; total = response.total || list.length; }
        else if (response?.stations) { list = response.stations; total = response.total || list.length; }
        else if (response?.spaceports) { list = response.spaceports; total = response.total || list.length; }
        else if (response?.structures) { list = response.structures; total = response.total || list.length; }
      } else {
        if (response?.data && Array.isArray(response.data)) { list = response.data; total = response.meta?.total || list.length; }
        else if (Array.isArray(response)) { list = response; total = list.length; }
      }

      setAssets(list);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      console.error(`❌ Ошибка загрузки ${activeType}:`, error);
      showSnackbar(`Не удалось загрузить список: ${error.message || 'Неизвестная ошибка'}`, 'error');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, activeType, pagination.page, pagination.limit, searchTerm]);

  // ==========================================================================
  // Загрузка деталей
  // ==========================================================================
  const loadAssetDetails = async (asset) => {
    setLoading(true);
    try {
      const config = ASSET_CONFIG[activeType];
      if (!config) throw new Error(`Unknown asset type: ${activeType}`);

      if (config.isAggregate) {
        const sourceType = asset.source_collection || asset.scheme_type || asset.type;
        const map = {
          shipyard: 'getShipyardScheme',
          spaceport: 'getSpaceportScheme',
          station: 'getStationScheme',
          civil_structure: 'getCivilStructureScheme',
          military_structure: 'getMilitaryStructureScheme',
        };
        const methodName = map[sourceType];
        if (!methodName) throw new Error(`No method for source type: ${sourceType}`);
        const method = konstructGateway[methodName];
        if (!method) throw new Error(`Method ${methodName} not found`);
        const response = await method.call(konstructGateway, asset.id);
        const data = response?.status === 'success' ? response.data : response;
        setCurrentData(data);
        calculateStats(data);
        setEditorMode('view');
        setSelectedAsset(asset);
        setSelectedModule(null); // 🔥 сбрасываем модуль при смене структуры
        return;
      }

      const method = konstructGateway[config.getMethod];
      if (!method) throw new Error(`Method ${config.getMethod} not found`);

      const response = await method.call(konstructGateway, asset.id);
      const data = config.isActive
        ? (response?.status === 'success' ? response.data : response)
        : response;

      setCurrentData(data);
      calculateStats(data);
      setEditorMode('view');
      setSelectedAsset(asset);
      setSelectedModule(null); // 🔥 сбрасываем модуль при смене структуры

      if (config.isActive && config.checkModernizationMethod) {
        try {
          const m = await konstructGateway[config.checkModernizationMethod](asset.id);
          setModernizationInfo(m);
        } catch (e) { /* ignore */ }
      }
      if (config.isActive && config.getModulesMethod) {
        try {
          const mods = await konstructGateway[config.getModulesMethod](asset.id);
          setAvailableModules(mods?.data || mods || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      console.error('Error loading asset details:', error);
      showSnackbar(`Ошибка загрузки деталей: ${error.message || 'Неизвестная ошибка'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // Библиотека модулей
  // ==========================================================================
  const loadModuleLibrary = useCallback(async () => {
    setLoadingModules(true);
    try {
      const modules = await konstructGateway.getModuleTemplates({ limit: 1024 });
      setModuleLibrary(modules || []);
    } catch (error) {
      console.error('Ошибка загрузки библиотеки модулей:', error);
      showSnackbar('Не удалось загрузить библиотеку модулей', 'error');
    } finally {
      setLoadingModules(false);
    }
  }, []);

  // ==========================================================================
  // Эффекты
  // ==========================================================================
  useEffect(() => { loadAssets(); }, [loadAssets]);
  useEffect(() => { loadModuleLibrary(); }, [loadModuleLibrary]);

  useEffect(() => {
    if (viewMode === 'hangar') setActiveType('ship_scheme');
    else setActiveType('space_station');
    setSelectedAsset(null);
    setCurrentData(null);
    setActiveTab('assets');
    setSelectedModule(null);
  }, [viewMode]);

  // ==========================================================================
  // Генерация
  // ==========================================================================
  const openGeneration = (mode) => {
    if (!isScheme) return showSnackbar('Генерация только в Конструкторе чертежей', 'warning');
    if (!isLoggedIn) return showSnackbar('Требуется авторизация', 'warning');
    setGenerationMode(mode);
    setGenerationWizardOpen(true);
  };

  const handleGenerationComplete = async (result) => {
    setGenerationWizardOpen(false);
    if (result) {
      setCurrentData(result);
      calculateStats(result);
      setEditorMode('view');
      showSnackbar('Чертёж создан', 'success');
      await loadAssets();
    }
  };

  // ==========================================================================
  // Модернизация / конвертация
  // ==========================================================================
  const handleOpenModernization = () => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    setSelectedModules([]);
    setModernizationDialogOpen(true);
  };

  const handleApplyModernization = async () => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentData || selectedModules.length === 0) return;
    setSaving(true);
    try {
      const config = ASSET_CONFIG[activeType];
      if (!config.applyModernizationMethod) throw new Error('Modernization not supported');
      const modifications = { modules: selectedModules, apply_at: new Date().toISOString() };
      await konstructGateway[config.applyModernizationMethod](currentData.id, modifications);
      showSnackbar('Модернизация применена', 'success');
      setModernizationDialogOpen(false);
      await loadAssetDetails(currentData);
    } catch (error) {
      showSnackbar(`Ошибка модернизации: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToScheme = async () => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentData) return;
    setSaving(true);
    try {
      const config = ASSET_CONFIG[activeType];
      if (!config.convertMethod) throw new Error('Conversion not supported');
      const schemeName = window.prompt('Название схемы:', `${currentData.name} (схема)`);
      if (!schemeName) return;
      await konstructGateway[config.convertMethod](currentData.id, schemeName);
      showSnackbar('Конвертировано в схему', 'success');
      if (window.confirm('Переключиться в Конструктор?')) {
        setViewMode('hangar');
        setActiveType(`${config.moduleType}_scheme`);
      }
    } catch (error) {
      showSnackbar(`Ошибка конвертации: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================================
  // CRUD
  // ==========================================================================
  const handleSave = async () => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentData) return;
    setSaving(true);
    try {
      const config = ASSET_CONFIG[activeType];
      let savedData;
      if (editorMode === 'create' || !currentData.id) {
        const m = konstructGateway[config.createMethod];
        if (!m) throw new Error('create not supported');
        const resp = await m.call(konstructGateway, currentData);
        savedData = resp?.data || resp || currentData;
      } else {
        const m = konstructGateway[config.updateMethod];
        if (!m) throw new Error('update not supported');
        const resp = await m.call(konstructGateway, currentData.id, currentData);
        savedData = resp?.data || resp || currentData;
      }
      showSnackbar('Сохранено', 'success');
      setCurrentData(savedData);
      calculateStats(savedData);
      setEditorMode('view');
      loadAssets();
    } catch (error) {
      showSnackbar(`Ошибка сохранения: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentData) return;
    setSaving(true);
    try {
      const m = konstructGateway[ASSET_CONFIG[activeType].deleteMethod];
      if (!m) throw new Error('delete not supported');
      await m.call(konstructGateway, currentData.id);
      showSnackbar('Удалено', 'success');
      setDeleteDialogOpen(false);
      setCurrentData(null);
      setSelectedAsset(null);
      setSelectedModule(null);
      loadAssets();
    } catch (error) {
      showSnackbar(`Ошибка удаления: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async () => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentData || !cloneName) return;
    setSaving(true);
    try {
      const m = konstructGateway[ASSET_CONFIG[activeType].createMethod];
      if (!m) throw new Error('create not supported');
      const cloneData = {
        ...currentData,
        id: undefined,
        name: cloneName,
        status: isScheme ? 'draft' : (currentData.status || 'active'),
      };
      delete cloneData.id;
      delete cloneData.created_at;
      delete cloneData.updated_at;
      await m.call(konstructGateway, cloneData);
      showSnackbar('Скопировано', 'success');
      setCloneDialogOpen(false);
      setCloneName('');
      loadAssets();
    } catch (error) {
      showSnackbar(`Ошибка копирования: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = () => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    showSnackbar('Активация будет реализована позже', 'info');
    setActivateDialogOpen(false);
  };

  // ==========================================================================
  // Модули
  // ==========================================================================
  const handleAddModuleFromLibrary = (module) => {
    if (!currentData) return showSnackbar('Сначала выберите объект', 'warning');
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    const newModule = { ...module, id: `installed_${Date.now()}`, installation_date: new Date().toISOString() };
    const updatedModules = [...(currentData.installed_modules || []), newModule];
    const updatedData = { ...currentData, installed_modules: updatedModules, modules: updatedModules };
    setCurrentData(updatedData);
    calculateStats(updatedData);
    showSnackbar('Модуль добавлен из библиотеки', 'success');
  };

  const handleRemoveModule = (moduleId) => {
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    const updatedModules = (currentData.installed_modules || []).filter((m) => m.id !== moduleId);
    const updatedData = { ...currentData, installed_modules: updatedModules, modules: updatedModules };
    setCurrentData(updatedData);
    calculateStats(updatedData);
    showSnackbar('Модуль удалён', 'info');
  };

  const handleOpenModuleDesigner = () => {
    if (!isLoggedIn) return showSnackbar('Требуется авторизация', 'warning');
    setEditingModule(null);
    setModuleDesignerMode('create');
    setModuleDesignerOpen(true);
  };

  const handleEditModule = (module) => {
    if (!canManageModule(module)) return showSnackbar('Недостаточно прав', 'warning');
    setEditingModule(module);
    setModuleDesignerMode('edit');
    setModuleDesignerOpen(true);
  };

  // 🔥 Итерация 9.6: выбрать модуль для 3D-вьювера
  const handleViewModuleIn3D = (module) => {
    setSelectedModule(module);
    setViewTab('module');
  };

  const handleSaveCustomModule = (newModuleData) => {
    if (!currentData) return;
    if (!canManageCurrent) return showSnackbar('Недостаточно прав', 'warning');
    const currentModules = [...(currentData.installed_modules || [])];
    let updatedModules;
    if (editingModule && moduleDesignerMode === 'edit') {
      updatedModules = currentModules.map((m) => (m.id === editingModule.id ? { ...m, ...newModuleData } : m));
    } else {
      updatedModules = [...currentModules, newModuleData];
    }
    const updatedData = { ...currentData, installed_modules: updatedModules, modules: updatedModules };
    setCurrentData(updatedData);
    calculateStats(updatedData);
    setModuleDesignerOpen(false);
    setEditingModule(null);
    showSnackbar('Модуль сохранён', 'success');
  };

  const handlePageChange = (_, newPage) => setPagination((p) => ({ ...p, page: newPage + 1 }));
  const handleRowsChange = (e) => setPagination((p) => ({ ...p, limit: parseInt(e.target.value, 10), page: 1 }));

  // ==========================================================================
  // Детальная статистика
  // ==========================================================================
  const renderDetailedStats = () => {
    if (!statistics) return null;
    return (
      <Accordion sx={{ mt: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
            Детальная статистика и требования
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1 }}>
          <Grid container spacing={1}>
            {moduleSlots && (
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Модульные слоты</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {moduleSlots.used}/{moduleSlots.total} (свободно: {moduleSlots.free})
                  </Typography>
                </Paper>
              </Grid>
            )}
            {constructionRequirements && (
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Требования</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>Время: {constructionRequirements.time} ч</Typography>
                </Paper>
              </Grid>
            )}
            {maintenanceCost && (
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Обслуживание</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>В день: {maintenanceCost.daily} кр</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  // ==========================================================================
  // Не авторизован
  // ==========================================================================
  if (!isAuthenticated) {
    return (
      <PageShell>
        <PagePanel>
          <Box sx={{ p: 2 }}>
            <Alert severity="warning">Требуется авторизация</Alert>
          </Box>
        </PagePanel>
      </PageShell>
    );
  }

  const config = currentConfig;
  const showActivateButton = isScheme && currentData?.status !== 'active' && editorMode === 'view';
  const showModernizationButton = isActive && config?.checkModernizationMethod && modernizationInfo?.eligible !== false;

 const typeList = viewMode === 'hangar'
  ? ASSET_TYPES.filter((t) => t.key.endsWith('_scheme'))
  : ASSET_TYPES.filter((t) => !t.key.endsWith('_scheme'));
  
   // ==========================================================================
  // Шапка
  // ==========================================================================
  const headerPanel = (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 1, px: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} justifyContent="space-between">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              {viewMode === 'dock' ? <EngIcon sx={{ color: 'warning.main', fontSize: 20 }} /> : <ArchitectureIcon sx={{ color: 'primary.main', fontSize: 20 }} />}
              <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.05rem' }}>
                {viewMode === 'dock' ? 'Инженерный док' : 'Конструктор чертежей'}
              </Typography>
              {!canManageAll && (
                <Chip
                  icon={<LockIcon sx={{ fontSize: 12 }} />}
                  label="Ограниченный доступ"
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.6rem' } }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {viewMode === 'dock'
                ? 'Активные объекты: просмотр, модернизация (свои / админ)'
                : 'Чертежи: просмотр, генерация новых (свои / админ)'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            <Button
              size="small"
              variant={viewMode === 'hangar' ? 'contained' : 'outlined'}
              startIcon={<BuildIcon fontSize="small" />}
              onClick={() => { setViewMode('hangar'); setSelectedAsset(null); setCurrentData(null); }}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Конструктор
            </Button>
            <Button
              size="small"
              variant={viewMode === 'dock' ? 'contained' : 'outlined'}
              color="warning"
              startIcon={<DockIcon fontSize="small" />}
              onClick={() => { setViewMode('dock'); setSelectedAsset(null); setCurrentData(null); }}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Инженерный док
            </Button>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {viewMode === 'hangar' && isLoggedIn && (
              <>
                <Button size="small" variant="contained" color="primary" startIcon={<AutoAwesomeIcon fontSize="small" />} onClick={() => openGeneration('auto')} sx={{ fontSize: '0.7rem', py: 0.5 }}>
                  Авто
                </Button>
                <Button size="small" variant="contained" color="secondary" startIcon={<TuneIcon fontSize="small" />} onClick={() => openGeneration('semi-auto')} sx={{ fontSize: '0.7rem', py: 0.5 }}>
                  Полуавто
                </Button>
                <Button size="small" variant="outlined" startIcon={<HandymanIcon fontSize="small" />} onClick={() => openGeneration('manual')} sx={{ fontSize: '0.7rem', py: 0.5 }}>
                  Ручной
                </Button>
                <Button size="small" variant="contained" color="secondary" startIcon={<LibraryAddIcon fontSize="small" />} onClick={handleOpenModuleDesigner} sx={{ fontSize: '0.7rem', py: 0.5 }}>
                  Создать модуль
                </Button>
              </>
            )}

            <Tooltip title="Обновить">
              <span>
                <IconButton size="small" onClick={loadAssets} disabled={loading}>
                  {loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // JSX
  // ==========================================================================
  return (
    <PageShell>
      {headerPanel}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 0.75, overflow: 'hidden', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* ============ LEFT: список ============ */}
        <PagePanel sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
            <Tabs
              value={activeType}
              onChange={(_, v) => { setActiveType(v); setSelectedAsset(null); setCurrentData(null); setSelectedModule(null); }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 30,
                '& .MuiTab-root': {
                  minHeight: 30, fontSize: '0.65rem', textTransform: 'none',
                  py: 0.25, px: 1, minWidth: 'auto',
                },
              }}
            >
              {typeList.map(({ key, label, icon }) => (
                <Tab
                  key={key}
                  value={key}
                  icon={React.cloneElement(icon, { sx: { fontSize: 14 } })}
                  label={label}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75, minHeight: 0, flex: 1 }}>
            <TextField
              placeholder={viewMode === 'dock' ? 'Поиск активных...' : 'Поиск чертежей...'}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5 }} />,
                sx: { fontSize: '0.75rem' },
              }}
            />

            <Box sx={{ flex: 1, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 0 }}>
              {loading && assets.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
              ) : assets.length === 0 ? (
                <Alert severity="info" sx={{ m: 1, fontSize: '0.7rem' }}>
                  {viewMode === 'dock' ? 'Нет активных объектов' : 'Нет чертежей'}
                </Alert>
              ) : (
                <List dense disablePadding>
                  {assets.map((asset) => {
                    const isSelected = selectedAsset?.id === asset.id;
                    const own = getOwnership(asset, myCharacterIds);
                    return (
                      <ListItemButton
                        key={asset.id}
                        selected={isSelected}
                        onClick={() => loadAssetDetails(asset)}
                        sx={{
                          py: 0.5,
                          borderLeft: 3,
                          borderColor: isSelected ? 'primary.main' : 'transparent',
                        }}
                      >
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem', fontWeight: isSelected ? 700 : 500 }}>
                                {asset.name || asset.id}
                              </Typography>
                              <StatusChip status={asset.status} />
                              {own === 'system' && (
                                <Tooltip title="Системный объект">
                                  <LockIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                </Tooltip>
                              )}
                            </Stack>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem' }}>
                              {asset.ship_class || asset.type || asset.vehicle_type || '-'} •{' '}
                              {asset.size_tier || asset.size || '-'} • T{asset.tech_level || 1}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>

            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page - 1}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleRowsChange}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage=""
              sx={{
                borderTop: 1, borderColor: 'divider', minHeight: 32,
                '& .MuiTablePagination-toolbar': { minHeight: 32 },
                '& .MuiTablePagination-selectLabel': { display: 'none' },
              }}
            />
          </Box>
        </PagePanel>

        {/* ============ RIGHT: редактор / библиотека ============ */}
        <PagePanel sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                minHeight: 30,
                '& .MuiTab-root': {
                  minHeight: 30, fontSize: '0.7rem', textTransform: 'none',
                  py: 0.25, px: 1, minWidth: 'auto',
                },
              }}
            >
              <Tab value="assets" icon={<ArchitectureIcon fontSize="small" />} iconPosition="start" label={viewMode === 'dock' ? 'Активные объекты' : 'Конструктор'} />
              <Tab value="library" icon={<CategoryIcon fontSize="small" />} iconPosition="start" label={`Библиотека модулей (${moduleLibrary.length})`} />
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 1 }}>
            {activeTab === 'library' ? (
              <ModuleLibraryView
                modules={moduleLibrary}
                loading={loadingModules}
                onAddToScheme={handleAddModuleFromLibrary}
                onEditModule={handleEditModule}
                onCreateModule={handleOpenModuleDesigner}
                canCreate={isLoggedIn}
                canAddToScheme={isLoggedIn && !!currentData && canManageCurrent}
                canManageModule={canManageModule}
                myCharacterIds={myCharacterIds}
              />
            ) : currentData ? (
              <>
                {/* Тулбар объекта */}
                <Box sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent="space-between" sx={{ rowGap: 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      {viewMode === 'dock' ? <EngIcon sx={{ fontSize: 20, color: 'warning.main' }} /> : React.cloneElement(config.icon, { sx: { fontSize: 20 } })}
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>
                        {currentData.name}
                      </Typography>
                      <StatusChip status={currentData.status} />
                      {ownership === 'system' && (
                        <Chip label="Система" size="small" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                      )}
                      {ownership === 'other' && (
                        <Chip label="Чужое" size="small" color="default" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                      )}
                      {ownership === 'mine' && (
                        <Chip label="Моё" size="small" color="success" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                      )}
                      {currentData.in_world && (
                        <Chip icon={<CheckCircleIcon sx={{ fontSize: 12 }} />} label="В мире" size="small" color="success" sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.55rem' } }} />
                      )}
                    </Stack>

                    <Stack direction="row" spacing={0.25} flexWrap="wrap" useFlexGap>
                      {canManageCurrent && editorMode === 'view' && (
                        <Tooltip title="Редактировать">
                          <IconButton size="small" onClick={() => setEditorMode('edit')} sx={{ p: 0.5 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageCurrent && editorMode === 'view' && config.createMethod && (
                        <Tooltip title="Копировать">
                          <IconButton size="small" onClick={() => { setCloneName(`${currentData.name} (копия)`); setCloneDialogOpen(true); }} sx={{ p: 0.5 }}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageCurrent && config.deleteMethod && (
                        <Tooltip title="Удалить">
                          <IconButton size="small" color="error" onClick={() => setDeleteDialogOpen(true)} sx={{ p: 0.5 }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageCurrent && editorMode === 'edit' && (
                        <>
                          <Tooltip title="Отмена">
                            <IconButton size="small" onClick={() => { setEditorMode('view'); loadAssetDetails(selectedAsset); }} sx={{ p: 0.5 }}>
                              <PauseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Сохранить">
                            <IconButton size="small" color="primary" onClick={handleSave} disabled={saving} sx={{ p: 0.5 }}>
                              {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {canManageCurrent && showActivateButton && (
                        <Tooltip title="Активировать">
                          <IconButton size="small" color="success" onClick={() => setActivateDialogOpen(true)} sx={{ p: 0.5 }}>
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageCurrent && showModernizationButton && (
                        <Tooltip title="Модернизация">
                          <IconButton size="small" color="warning" onClick={handleOpenModernization} sx={{ p: 0.5 }}>
                            <HandymanIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canManageCurrent && isActive && config.convertMethod && (
                        <Tooltip title="Конвертировать в схему">
                          <IconButton size="small" color="info" onClick={handleConvertToScheme} sx={{ p: 0.5 }}>
                            <LibraryAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>

                  {statistics && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75, rowGap: 0.5 }}>
                      <StatCard compact title="Масса" value={(statistics.mass / 1000).toFixed(1)} subtitle="т" icon={<WeightIcon fontSize="small" />} color="warning" />
                      <StatCard compact title="Стоимость" value={(statistics.cost / 1000).toFixed(0)} subtitle="к" icon={<CostIcon fontSize="small" />} color="success" />
                      <StatCard compact title="Баланс" value={`${statistics.powerBalance >= 0 ? '+' : ''}${statistics.powerBalance}`} subtitle="Вт" icon={<PowerIcon fontSize="small" />} color={statistics.powerBalance >= 0 ? 'info' : 'error'} />
                      <StatCard compact title="Экипаж" value={statistics.crew} subtitle="чел" icon={<PeopleIcon fontSize="small" />} color="secondary" />
                      <StatCard compact title="TL" value={currentData.tech_level || 1} icon={<ScienceIcon fontSize="small" />} color="primary" />
                    </Stack>
                  )}

                  {modernizationInfo && modernizationInfo.eligible !== false && (
                    <Alert severity="info" sx={{ mt: 0.75, fontSize: '0.7rem', py: 0.25 }}>
                      Доступна модернизация: {modernizationInfo.message || '—'}
                      {availableModules.length > 0 && ` • модулей: ${availableModules.length}`}
                    </Alert>
                  )}
                  {!canManageCurrent && ownership !== 'none' && (
                    <Alert severity="warning" sx={{ mt: 0.75, fontSize: '0.7rem', py: 0.25 }}>
                      {ownership === 'system'
                        ? 'Системный объект — редактирование только для администратора.'
                        : 'Чужой объект — редактирование только для владельца или администратора.'}
                    </Alert>
                  )}

                  {renderDetailedStats()}
                </Box>

                <Divider sx={{ mb: 1 }} />

                {/* 🔥 Итерация 9.6: Tabs «Структура / Модуль» */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                  <Tabs
                    value={viewTab}
                    onChange={(_, v) => setViewTab(v)}
                    sx={{
                      minHeight: 28,
                      '& .MuiTab-root': {
                        minHeight: 28, fontSize: '0.7rem', textTransform: 'none',
                        py: 0.25, px: 1.5, minWidth: 'auto',
                      },
                    }}
                  >
                    <Tab
                      value="structure"
                      icon={<ArchitectureIcon sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                      label="Структура"
                    />
                    <Tab
                      value="module"
                      icon={<ViewInArIcon sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                      label="Модуль"
                      disabled={!selectedModule}
                    />
                  </Tabs>
                </Box>

                {/* 🔥 Содержимое: StructureViewer или Module3DViewer */}
                <Box sx={{ mb: 1 }}>
                  {viewTab === 'structure' ? (
                    <StructureViewer
                      type={config?.moduleType || 'structure'}
                      id={currentData.id}
                      mode="object"
                    />
                  ) : (
                    selectedModule && (
                      <Module3DViewer
                        module={selectedModule}
                        height="400px"
                        interactive
                        showInfo
                        onClose={() => setSelectedModule(null)}
                      />
                    )
                  )}
                </Box>

                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <StructureShipEditor
                      data={currentData}
                      onChange={setCurrentData}
                      isLocked={!canManageCurrent}
                      isDockMode={viewMode === 'dock'}
                      sizeTiers={SIZE_TIERS}
                      techLevels={TECH_LEVELS}
                      statuses={viewMode === 'hangar' ? SCHEME_STATUSES : ACTIVE_STATUSES}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ModuleEditor
                      modules={currentData.installed_modules || []}
                      onAddModule={() => setAddModuleDialog(true)}
                      onCreateCustom={handleOpenModuleDesigner}
                      onEditModule={handleEditModule}
                      onRemoveModule={handleRemoveModule}
                      canEdit={canManageCurrent}
                      isDockMode={viewMode === 'dock'}
                      moduleLibrary={moduleLibrary}
                      onAddFromLibrary={handleAddModuleFromLibrary}
                      maxSlots={moduleSlots?.total || 10}
                      usedSlots={moduleSlots?.used || 0}
                      onViewModule={handleViewModuleIn3D}
                    />
                  </Grid>
                </Grid>
              </>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary', py: 4 }}>
                {viewMode === 'dock' ? <EngIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} /> : <ArchitectureIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />}
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Объект не выбран</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  {viewMode === 'dock' ? 'Выберите активный объект из списка' : 'Выберите чертёж из списка или создайте новый'}
                </Typography>
              </Box>
            )}
          </Box>
        </PagePanel>
      </Box>

      {/* ============ МОДАЛКИ ============ */}

      {isLoggedIn && (
        <GenerationWizard
          open={generationWizardOpen}
          onClose={() => setGenerationWizardOpen(false)}
          onComplete={handleGenerationComplete}
          type={currentConfig?.moduleType || activeType.replace('_scheme', '')}
          mode={generationMode}
          moduleLibrary={moduleLibrary}
          initialData={currentData}
        />
      )}

      <Dialog open={modernizationDialogOpen} onClose={() => setModernizationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>Модернизация</DialogTitle>
        <DialogContent>
          {availableModules.length === 0 ? (
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>Нет доступных модулей</Alert>
          ) : (
            <List dense>
              {availableModules.map((module) => (
                <ListItemButton key={module.id} divider>
                  <ListItemText
                    primary={<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{module.name}</Typography>}
                    secondary={<Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Тип: {module.type} • TL{module.tech_level}</Typography>}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedModules([...selectedModules, module]);
                      setAvailableModules(availableModules.filter((m) => m.id !== module.id));
                    }}
                    sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 22 }}
                  >
                    Добавить
                  </Button>
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModernizationDialogOpen(false)} size="small" sx={{ fontSize: '0.7rem' }}>Отмена</Button>
          <Button onClick={handleApplyModernization} variant="contained" color="warning" size="small" disabled={selectedModules.length === 0 || saving} sx={{ fontSize: '0.7rem' }}>
            Применить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addModuleDialog} onClose={() => setAddModuleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>Добавить модуль из библиотеки</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={moduleLibrary}
            getOptionLabel={(opt) => opt.name || opt.purpose || ''}
            renderInput={(params) => <TextField {...params} label="Модуль" size="small" sx={{ mt: 1 }} />}
            onChange={(_, val) => {
              if (val) { handleAddModuleFromLibrary(val); setAddModuleDialog(false); }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModuleDialog(false)} size="small" sx={{ fontSize: '0.7rem' }}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {canManageModule(editingModule || {}) || moduleDesignerMode === 'create' ? (
        <ModuleDesigner
          open={moduleDesignerOpen}
          onClose={() => { setModuleDesignerOpen(false); setEditingModule(null); }}
          onSave={handleSaveCustomModule}
          initialData={editingModule}
          mode={moduleDesignerMode}
          availableModules={moduleLibrary}
          sizeTiers={SIZE_TIERS}
          techLevels={TECH_LEVELS}
        />
      ) : null}

      <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>Активация</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
            Активировать <b>{currentData?.name}</b>?
          </Typography>
          <Alert severity="warning" sx={{ fontSize: '0.7rem' }}>
            После активации объект перейдёт в симуляцию.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)} size="small" sx={{ fontSize: '0.7rem' }}>Отмена</Button>
          <Button onClick={handleActivate} variant="contained" color="success" size="small" sx={{ fontSize: '0.7rem' }}>Активировать</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontSize: '1rem' }}>Удалить?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            Удалить <b>{currentData?.name}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.7rem' }}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained" size="small" disabled={saving} sx={{ fontSize: '0.7rem' }}>Удалить</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>Копировать</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название копии"
            fullWidth
            size="small"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)} size="small" sx={{ fontSize: '0.7rem' }}>Отмена</Button>
          <Button onClick={handleClone} variant="contained" size="small" disabled={!cloneName || saving} sx={{ fontSize: '0.7rem' }}>Создать</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </PageShell>
  );
}